<script setup lang="ts">
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { ContractDTO } from "@/models/dtos/contract-dto";
import type { ErrorReportMetaDTO } from "@/models/dtos/error-report-meta-dto";
import type { ErrorReportMetaRequest } from "@/models/requests/error-report-meta-request";
import type { PhoneServiceMetaDTO } from "@/models/dtos/phone-service-meta-dto";
import type { PhoneServiceMetaRequest } from "@/models/requests/phone-service-meta-request";
import type { UpdateContractRequest } from "@/models/requests/update-contract-request";

import { getUpdateContractSchema } from "@/schemas/update-contract-schema";
import { contractsService } from "@/services/contracts-service";
import { notificationsService } from "@/services/notifications-service";

import { cloneDeep } from "lodash-es";
import { onBeforeMount, ref, toRefs } from "vue";
import { useLoggedInUserFlags } from "@/composables/use-logged-in-user-flags.ts";

const { userIsOneAdmin } = useLoggedInUserFlags("0");

function getDefaultContractFormData(
  contract?: ContractDTO,
): UpdateContractRequest {
  return {
    ordersFromDate: contract?.ordersFromDate ?? undefined,
    phoneService: contract?.phoneService ?? false,
    phoneServiceMeta: undefined,
  };
}

function getDefaultPhoneServiceFormData(
  phoneContractMeta?: PhoneServiceMetaDTO,
): PhoneServiceMetaRequest {
  return {
    contractDescription: phoneContractMeta?.contractDescription ?? undefined,
    phonenumber: phoneContractMeta?.phonenumber ?? undefined,
    notes: phoneContractMeta?.notes ?? undefined,
  };
}

const props = defineProps<{
  customerNumber: string;
  contractId: number;
}>();
const { contractId } = toRefs(props);

const pageLoading = ref(false);
const loading = ref(false);

const contractFormData = ref(getDefaultContractFormData());
const contract = ref<ContractDTO | undefined>();

const phoneServiceFormData = ref(getDefaultPhoneServiceFormData());
const fileName = ref<string | undefined>();
const base64ImageData = ref<string | undefined>();

const { result, validate } = useControlValidation(() =>
  getUpdateContractSchema(),
);

onBeforeMount(async () => {
  pageLoading.value = true;

  await loadContract();

  pageLoading.value = false;
});

async function loadContract() {
  try {
    contract.value = await contractsService.getContract(contractId.value);
    const errorReportMeta = contract.value?.errorReportMeta;
    const phoneServiceMeta = contract.value?.phoneServiceMeta;

    contractFormData.value = getDefaultContractFormData(contract.value);
    phoneServiceFormData.value =
      getDefaultPhoneServiceFormData(phoneServiceMeta);
    fileName.value = errorReportMeta?.imageName ?? "";
    base64ImageData.value = errorReportMeta?.base64ImageData ?? "";
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av kontraktet.",
      type: "error",
      error,
    });
  }
}

async function onUpdate() {
  loading.value = true;

  try {
    const valid = validate(contractFormData.value);
    const contractId = contract.value?.id;
    if (!contractId || !valid) return;
    const request: UpdateContractRequest = cloneDeep(contractFormData.value);

    if (!request.ordersFromDate) {
      request.ordersFromDate = undefined;
    }

    if (request.phoneService) {
      request.phoneServiceMeta = {
        contractDescription:
          phoneServiceFormData.value.contractDescription || undefined,
        notes: phoneServiceFormData.value.notes || undefined,
        phonenumber: phoneServiceFormData.value.phonenumber || undefined,
      };
    } else {
      request.phoneServiceMeta = undefined;
    }

    const updated = await contractsService.updateContract(contractId, request);
    contract.value = updated;
    notificationsService.open({
      text: "Affär uppdaterad.",
      type: "success",
    });
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid uppdatering av kontraktet.",
      type: "error",
      error,
    });
  } finally {
    loading.value = false;
  }
}

function onChangePhoneService() {
  if (!contractFormData.value.phoneService) {
    phoneServiceFormData.value = getDefaultPhoneServiceFormData();
  }
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">{{ contract?.name }}</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else class="columns">
      <div class="column">
        <form @submit.prevent="onUpdate">
          <div class="field">
            <label class="label" for="ordersFromDate"
              >Hämta serviceärenden från och med</label
            >
            <control-validation
              :result="result"
              field="ordersFromData"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="ordersFromDate"
                  v-model="contractFormData.ordersFromDate"
                  class="input"
                  type="datetime-local"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div v-if="userIsOneAdmin" class="field">
            <control-validation
              :result="result"
              field="phoneService"
              #="{ inputClass }"
            >
              <div class="control">
                <label class="checkbox" for="phoneService">
                  <input
                    id="phoneService"
                    v-model="contractFormData.phoneService"
                    type="checkbox"
                    :class="inputClass"
                    @change="onChangePhoneService"
                  />
                  Stöd för anmälan via telefon
                </label>
              </div>
            </control-validation>
          </div>

          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :class="{ 'is-loading': loading }"
            >
              Uppdatera
            </button>
          </div>
        </form>
      </div>

      <div
        v-if="contractFormData.phoneService"
        class="column notification is-narrow"
      >
        <p class="title is-5">Stöd för telefontjänst</p>

        <form @submit.prevent>
          <div class="field">
            <label class="label" for="phoneServiceDescription"
              >Beskrivning</label
            >
            <div class="control">
              <textarea
                id="phoneServiceDescription"
                v-model="phoneServiceFormData.contractDescription"
                class="textarea"
              ></textarea>
            </div>
          </div>
          <div class="field">
            <label class="label" for="contract.phone">Telefonnummer</label>
            <div class="control has-icons-right">
              <input
                id="contract.phone"
                v-model="phoneServiceFormData!.phonenumber"
                class="input"
                type="tel"
                name="contract.phone"
              />
            </div>
          </div>
          <div class="field">
            <label class="label" for="phoneServiceNotes">Information</label>
            <div class="control">
              <textarea
                id="phoneServiceNotes"
                v-model="phoneServiceFormData.notes"
                class="textarea"
              ></textarea>
            </div>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.preview-image {
  max-width: 16vw;
}
</style>
